/**
 * cart-bundle.js
 *
 * Hydrates bundle component lists rendered by the bundle-components-list
 * snippet. Finds <li data-variant-gid="..."> placeholders inside a
 * <ul class="bundle-components"> element, resolves each GID to a display
 * label via the Storefront API, and paints the label into the <li>.
 *
 * Reuses the sessionStorage cache written by product-bundle.js so that
 * customers who've already visited a bundle product page get instant
 * hydration in the cart.
 *
 * Re-runs whenever the cart drawer or cart page contents change (e.g.
 * after an AJAX cart update), via a MutationObserver on the cart roots.
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Shared config (kept in sync with product-bundle.js)
  // ---------------------------------------------------------------------------

  var STOREFRONT_API_VERSION = '2024-10';
  var CACHE_KEY_PREFIX = 'bundle-picker-cache-v1:';

  // Label trimming — keep in sync with product-bundle.js so PDP and cart
  // display the same labels.
  var LABEL_TRIM_MARKERS = [
    '4 Pack'
  ];

  function trimLabel(label) {
    if (!label) return label;
    for (var i = 0; i < LABEL_TRIM_MARKERS.length; i++) {
      var idx = label.indexOf(LABEL_TRIM_MARKERS[i]);
      if (idx > -1) {
        return label.substring(0, idx).trim();
      }
    }
    return label;
  }

  // ---------------------------------------------------------------------------
  // Storefront token resolution
  // ---------------------------------------------------------------------------

  function getStorefrontToken() {
    var picker = document.querySelector('[data-bundle-picker][data-storefront-token]');
    if (picker) return picker.getAttribute('data-storefront-token');

    var meta = document.querySelector('meta[name="shopify-storefront-api-token"]');
    if (meta && meta.content) return meta.content;

    if (window.Shopify && window.Shopify.storefrontToken) {
      return window.Shopify.storefrontToken;
    }

    return null;
  }

  function storefrontEndpoint() {
    return '/api/' + STOREFRONT_API_VERSION + '/graphql.json';
  }

  // ---------------------------------------------------------------------------
  // Cache helpers (read-only here — product-bundle.js writes)
  // ---------------------------------------------------------------------------

  /**
   * Scan all sessionStorage cache entries and build one unified lookup
   * keyed by variant GID. Cheap — typically a handful of entries.
   */
  function readAllCached() {
    var map = new Map();
    try {
      for (var i = 0; i < sessionStorage.length; i++) {
        var key = sessionStorage.key(i);
        if (!key || key.indexOf(CACHE_KEY_PREFIX) !== 0) continue;
        var raw = sessionStorage.getItem(key);
        if (!raw) continue;
        var parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') continue;
        Object.keys(parsed).forEach(function (gid) {
          if (!map.has(gid)) map.set(gid, parsed[gid]);
        });
      }
    } catch (e) {
      // Ignore parse errors — fall through to fetch.
    }
    return map;
  }

  /**
   * Write a freshly-fetched batch to a dedicated cart cache entry so we
   * don't have to refetch on subsequent cart renders in this session.
   */
  function writeCartCache(map) {
    if (!map || map.size === 0) return;
    try {
      var key = CACHE_KEY_PREFIX + 'cart';
      var existing = {};
      try {
        var raw = sessionStorage.getItem(key);
        if (raw) existing = JSON.parse(raw) || {};
      } catch (e) {}
      map.forEach(function (value, gid) { existing[gid] = value; });
      sessionStorage.setItem(key, JSON.stringify(existing));
    } catch (e) {
      // Quota / disabled / etc. — fail silently.
    }
  }

  // ---------------------------------------------------------------------------
  // Fetch missing GIDs from Storefront API
  // ---------------------------------------------------------------------------

  function fetchVariantData(gids) {
    if (!gids.length) return Promise.resolve(new Map());

    var token = getStorefrontToken();
    if (!token) {
      console.warn('[cart-bundle] No Storefront API token found — component labels will not hydrate.');
      return Promise.resolve(new Map());
    }

    var query = [
      'query($ids: [ID!]!) {',
      '  nodes(ids: $ids) {',
      '    ... on ProductVariant {',
      '      id',
      '      title',
      '      product { title }',
      '    }',
      '  }',
      '}'
    ].join('\n');

    return fetch(storefrontEndpoint(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
        'Accept': 'application/json'
      },
      body: JSON.stringify({ query: query, variables: { ids: gids } })
    })
      .then(function (resp) {
        if (!resp.ok) throw new Error('HTTP ' + resp.status);
        return resp.json();
      })
      .then(function (data) {
        if (data.errors) {
          console.warn('[cart-bundle] Storefront API errors:', data.errors);
        }
        var nodes = (data && data.data && data.data.nodes) || [];
        var map = new Map();
        nodes.forEach(function (node) {
          if (!node || !node.id) return;
          var productTitle = (node.product && node.product.title) || '';
          var variantTitle = node.title || '';
          var label = productTitle;
          if (variantTitle && variantTitle !== 'Default Title') {
            label = label ? (productTitle + ' — ' + variantTitle) : variantTitle;
          }
          map.set(node.id, {
            label: trimLabel(label) || node.id
          });
        });
        writeCartCache(map);
        return map;
      })
      .catch(function (err) {
        console.warn('[cart-bundle] Hydration failed:', err);
        return new Map();
      });
  }

  // ---------------------------------------------------------------------------
  // Hydration
  // ---------------------------------------------------------------------------

  /**
   * Paint a resolved label into an <li> placeholder. Respects the quantity
   * prefix when > 1.
   */
  function paintItem(li, label) {
    var qty = parseInt(li.getAttribute('data-quantity'), 10) || 1;
    var labelEl = li.querySelector('.bundle-components__label');
    if (!labelEl) return;
    labelEl.textContent = qty + ' × ' + label;
  }

  /**
   * Find all unhydrated items, collect their GIDs, check cache, fetch
   * the rest, paint them all.
   */
  function hydrateAll(root) {
    root = root || document;
    var items = root.querySelectorAll('.bundle-components__item[data-variant-gid]');
    if (!items.length) return;

    // Collect unhydrated items grouped by GID (multiple items may reference
    // the same variant — e.g. the same component appearing in multiple cart
    // lines or multiple bundles).
    var gidSet = {};
    var unhydrated = [];
    for (var i = 0; i < items.length; i++) {
      var li = items[i];
      if (li.hasAttribute('data-hydrated')) continue;
      var gid = li.getAttribute('data-variant-gid');
      if (!gid) continue;
      gidSet[gid] = true;
      unhydrated.push(li);
    }

    if (!unhydrated.length) return;

    var cached = readAllCached();
    var toFetch = [];
    Object.keys(gidSet).forEach(function (gid) {
      if (!cached.has(gid)) toFetch.push(gid);
    });

    // Helper to apply cached/resolved data to the unhydrated elements
    function apply(dataMap) {
      unhydrated.forEach(function (li) {
        var gid = li.getAttribute('data-variant-gid');
        var data = dataMap.get(gid) || cached.get(gid);
        if (data && data.label) {
          paintItem(li, data.label);
          li.setAttribute('data-hydrated', '1');
        } else {
          // Leave the ellipsis placeholder as-is; next render will retry.
        }
      });
    }

    if (toFetch.length === 0) {
      apply(new Map());
      return;
    }

    fetchVariantData(toFetch).then(apply);
  }

  // ---------------------------------------------------------------------------
  // Mount + observe
  // ---------------------------------------------------------------------------

  function mountObservers() {
    // Watch both cart roots. Cart drawer + main cart.
    var roots = [
      document.querySelector('cart-drawer'),
      document.getElementById('main-cart-items'),
      document.getElementById('CartDrawer')
    ].filter(Boolean);

    roots.forEach(function (root) {
      var observer = new MutationObserver(function () {
        // Debounce slightly — multiple mutations often arrive together
        // during an AJAX cart update.
        if (observer._timer) clearTimeout(observer._timer);
        observer._timer = setTimeout(function () { hydrateAll(root); }, 50);
      });
      observer.observe(root, { childList: true, subtree: true });
    });
  }

  function init() {
    hydrateAll();
    mountObservers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
